import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
} from '../../sectepe-ui/index';

/**
 * Ersatz fuer window.confirm / window.prompt: ein Dialog auf Basis des
 * Kit-Modals, der als Promise antwortet. `confirm('Text')` reicht fuer den
 * haeufigsten Fall; `prompt` fragt zusaetzlich einen Wert ab.
 */
export interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  /** Default 'Bestätigen'. */
  confirmLabel?: string;
  /** Default 'Abbrechen'. */
  cancelLabel?: string;
  /** 'danger' faerbt den Bestaetigen-Knopf rot (Loeschen, Trennen). */
  tone?: 'default' | 'danger';
}

export interface PromptOptions extends ConfirmOptions {
  label: string;
  placeholder?: string;
  defaultValue?: string;
  multiline?: boolean;
  /** Leerer Wert kann nicht bestaetigt werden. */
  required?: boolean;
}

export interface ConfirmApi {
  confirm: (opts: ConfirmOptions | string) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

type Pending =
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'prompt'; opts: PromptOptions; resolve: (v: string | null) => void };

export const ConfirmContext = createContext<ConfirmApi | null>(null);

export function useConfirm(): ConfirmApi {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm() muss innerhalb von <ConfirmProvider> verwendet werden.');
  }
  return ctx;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<Pending[]>([]);
  const [value, setValue] = useState('');
  const current = queue[0];
  const fieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const api = useMemo<ConfirmApi>(() => ({
    confirm: (opts) =>
      new Promise<boolean>((resolve) => {
        const normalized = typeof opts === 'string' ? { title: opts } : opts;
        setQueue((q) => [...q, { kind: 'confirm', opts: normalized, resolve }]);
      }),
    prompt: (opts) =>
      new Promise<string | null>((resolve) => {
        setQueue((q) => [...q, { kind: 'prompt', opts, resolve }]);
      }),
  }), []);

  // Beim Wechsel des aktuellen Dialogs Eingabefeld vorbelegen und fokussieren.
  useEffect(() => {
    if (!current) return;
    setValue(current.kind === 'prompt' ? current.opts.defaultValue ?? '' : '');
    const id = window.setTimeout(() => fieldRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [current]);

  const settle = useCallback((result: boolean | string | null) => {
    setQueue((q) => {
      const [head, ...rest] = q;
      if (head) {
        if (head.kind === 'confirm') head.resolve(Boolean(result));
        else head.resolve(typeof result === 'string' ? result : null);
      }
      return rest;
    });
  }, []);

  const cancel = () => settle(current?.kind === 'prompt' ? null : false);

  // Escape gehoert dem obersten Dialog: das Kit-Modal haengt je offener
  // Instanz einen keydown-Listener an document, ohne Stapel. Oeffnet eine
  // Seite die Rueckfrage aus einem Formular-Modal heraus, schloss Escape
  // sonst beide und verwarf die Eingaben. Darum faengt der Provider Escape
  // in der Capture-Phase ab, bricht nur die Rueckfrage ab und stoppt die
  // Weitergabe.
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;
  useEffect(() => {
    if (!current) return;
    const aufEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      cancelRef.current();
    };
    document.addEventListener('keydown', aufEscape, true);
    return () => document.removeEventListener('keydown', aufEscape, true);
  }, [current]);
  const trimmed = value.trim();
  const blocked = current?.kind === 'prompt' && current.opts.required && trimmed.length === 0;
  const ok = () => {
    if (!current || blocked) return;
    settle(current.kind === 'prompt' ? value : true);
  };

  const onFieldKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if (current?.kind === 'prompt' && current.opts.multiline && !(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    ok();
  };

  const opts = current?.opts;
  const tone = opts?.tone ?? 'default';

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      <Modal open={Boolean(current)} onOpenChange={(open) => { if (!open) cancel(); }}>
        {current && opts && (
          <ModalContent className="ui-confirm">
            <ModalHeader>
              <ModalTitle>{opts.title}</ModalTitle>
              {opts.message && <ModalDescription>{opts.message}</ModalDescription>}
            </ModalHeader>
            {current.kind === 'prompt' && (
              <ModalBody>
                <label className="form-label" htmlFor="ui-confirm-field">{current.opts.label}</label>
                {current.opts.multiline ? (
                  <textarea
                    id="ui-confirm-field"
                    ref={fieldRef as React.RefObject<HTMLTextAreaElement>}
                    className="form-textarea"
                    rows={4}
                    value={value}
                    placeholder={current.opts.placeholder}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={onFieldKeyDown}
                  />
                ) : (
                  <input
                    id="ui-confirm-field"
                    ref={fieldRef as React.RefObject<HTMLInputElement>}
                    className="form-input"
                    type="text"
                    value={value}
                    placeholder={current.opts.placeholder}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={onFieldKeyDown}
                  />
                )}
                {current.opts.multiline && (
                  <div className="form-helper">Strg+Enter bestätigt.</div>
                )}
              </ModalBody>
            )}
            <ModalFooter>
              <button type="button" className="btn btn-secondary" onClick={cancel}>
                {opts.cancelLabel ?? 'Abbrechen'}
              </button>
              <button
                type="button"
                className={`btn ${tone === 'danger' ? 'btn-danger' : 'btn-primary'}`}
                onClick={ok}
                disabled={Boolean(blocked)}
                autoFocus={current.kind === 'confirm'}
              >
                {opts.confirmLabel ?? 'Bestätigen'}
              </button>
            </ModalFooter>
          </ModalContent>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
};
